import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import api from '../../utils/axios';

import { Webview } from '../Webview';
import { WebviewToolbar } from '../WebviewToolbar';

interface PreviewPanelProps {
  projectId: string;
  promptVersion?: number | null;
  onClose: () => void;
}

interface ProjectUrls {
  id: string;
  project_id: string;
  url_lines: string[];
  last_updated_from_thread_id?: string;
  last_updated_from_message_id?: string;
  created_at: number;
  updated_at: number;
  user_id: string;
}

interface PortsResponse {
  project_id: string;
  ports: Record<string, { port: number; is_primary: boolean }>;
  message?: string;
}

const PreviewPanel: React.FC<PreviewPanelProps> = React.memo(function PreviewPanel({
  projectId,
  promptVersion,
  onClose,
}) {
  const [initialUrl, setInitialUrl] = useState<string>('');
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [noUrlAvailable, setNoUrlAvailable] = useState(false);
  const [isHttpBlocked, setIsHttpBlocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Append a unique cache-busting param to force the browser to bypass cache
  const cacheBust = (url: string): string => {
    try {
      const u = new URL(url);
      u.searchParams.set('_cb', Date.now().toString());
      return u.toString();
    } catch {
      return url;
    }
  };

  // Memoize the cache-busted URL — recomputes ONLY when currentUrl or reloadKey changes,
  // NOT on every parent re-render. This prevents the iframe from reloading on every keystroke.
  const stableBustedUrl = useMemo(() => cacheBust(currentUrl), [currentUrl, reloadKey]);

  // Extract URL from URL line (same logic as ApplicationUrlsModal)
  const extractUrlFromLine = (urlLine: string): string | null => {
    const colonIndex = urlLine.indexOf(':');
    if (colonIndex === -1) return null;
    
    const url = urlLine.substring(colonIndex + 1).trim();
    return url.startsWith('http') ? url : null;
  };


  // Fetch ports and application URLs on mount
  useEffect(() => {
    const fetchAndLoadUrl = async () => {
      try {
        setIsLoading(true);
        setNoUrlAvailable(false);

        const isModernProject = (promptVersion ?? 0) >= 2;

        if (isModernProject) {
          // ─── Modern (v2+): Single URL through nginx gateway ───
          // Only need to fetch URLs — no port matching required
          const urlsResponse = await api.get<ProjectUrls | null>(`/projects/${projectId}/urls`);
          const urls = urlsResponse.data;

          if (!urls || !urls.url_lines || urls.url_lines.length === 0) {
            console.log('⚠️ [Preview] No application URLs found for v2 project');
            setNoUrlAvailable(true);
            setIsLoading(false);
            return;
          }

          // v2 projects produce a single application URL — just use the first one
          const foundUrl = extractUrlFromLine(urls.url_lines[0]);

          if (foundUrl) {
            console.log('✅ [Preview] v2 project URL:', foundUrl);
            // Block HTTP URLs when platform runs on HTTPS (mixed content)
            if (window.location.protocol === 'https:' && foundUrl.startsWith('http:')) {
              console.log('⚠️ [Preview] HTTP URL blocked on HTTPS platform:', foundUrl);
              setIsHttpBlocked(true);
              setCurrentUrl(foundUrl);
              setIsLoading(false);
              return;
            }
            setIsHttpBlocked(false);
            setInitialUrl(foundUrl);
            setCurrentUrl(foundUrl);
          } else {
            console.log('⚠️ [Preview] Could not extract URL from:', urls.url_lines[0]);
            setNoUrlAvailable(true);
          }
        } else {
          // ─── Legacy (v1): Match primary port against URL lines ───
          const [portsResponse, urlsResponse] = await Promise.all([
            api.get<PortsResponse>(`/projects/${projectId}/ports`),
            api.get<ProjectUrls | null>(`/projects/${projectId}/urls`)
          ]);

          const ports = portsResponse.data.ports;
          const urls = urlsResponse.data;

          // Find the primary port
          let primaryPort: number | null = null;
          if (ports && Object.keys(ports).length > 0) {
            for (const [serviceType, portInfo] of Object.entries(ports)) {
              if (portInfo.is_primary) {
                primaryPort = portInfo.port;
                console.log(`✅ [Preview] Found primary port ${primaryPort} for service: ${serviceType}`);
                break;
              }
            }
          }

          if (!primaryPort) {
            console.log('⚠️ [Preview] No primary port found for legacy project');
            setNoUrlAvailable(true);
            setIsLoading(false);
            return;
          }

          if (!urls || !urls.url_lines || urls.url_lines.length === 0) {
            console.log('⚠️ [Preview] No application URLs found for legacy project');
            setNoUrlAvailable(true);
            setIsLoading(false);
            return;
          }

          // Match URLs against primary port
          let foundUrl: string | null = null;
          for (const urlLine of urls.url_lines) {
            const url = extractUrlFromLine(urlLine);
            if (url && url.includes(`:${primaryPort}`)) {
              foundUrl = url;
              break;
            }
          }

          if (foundUrl) {
            console.log('✅ [Preview] Legacy URL with primary port:', foundUrl);
            // Block HTTP URLs when platform runs on HTTPS (mixed content)
            if (window.location.protocol === 'https:' && foundUrl.startsWith('http:')) {
              console.log('⚠️ [Preview] HTTP URL blocked on HTTPS platform:', foundUrl);
              setIsHttpBlocked(true);
              setCurrentUrl(foundUrl);
              setIsLoading(false);
              return;
            }
            setIsHttpBlocked(false);
            setInitialUrl(foundUrl);
            setCurrentUrl(foundUrl);
          } else {
            console.log('⚠️ [Preview] No URL found matching primary port:', primaryPort);
            setNoUrlAvailable(true);
          }
        }

        setIsLoading(false);
       
      } catch (error: any) {
        console.error('❌ [Preview] Failed to fetch project data:', error);
        setNoUrlAvailable(true);
        setIsLoading(false);
      }
    };

    if (projectId) {
      fetchAndLoadUrl();
    }
  }, [projectId, promptVersion]);

  // Handle webview load complete
  const handleWebviewLoad = () => {
    console.log('✅ Webview loaded successfully');
    setIsLoading(false);
  };

// Handle home button click (for toolbar)
  const handleHomeClick = () => {
    if (initialUrl) {
      setIsLoading(true);
      setCurrentUrl(initialUrl);
      setReloadKey(Date.now());
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-bg-primary rounded-bg-v2-lg container-shadow-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between min-h-[60px] max-h-[60px] px-bg-v2-xl bg-bg-primary border-b border-border-secondary">
        <div className="flex items-center gap-bg-v2-lg flex-1">
          <Icon name={IconName.Layout} size="sm" variant="secondary" />
          <Text variant="text-sm" weight="semibold" color="primary">
            Preview
          </Text>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200 flex-shrink-0"
          aria-label="Close"
        >
          <Icon name={IconName.Cross} size="sm" inheritColor />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col items-start overflow-hidden bg-bg-primary min-h-0">
        {/* Webview Toolbar - only show if we have a URL */}
        {!noUrlAvailable && !isHttpBlocked && (
          <WebviewToolbar
            currentUrl={currentUrl}
            initialUrl={initialUrl}
            onHomeClick={handleHomeClick}
          />
        )}

        {/* Webview Content or No URL Message */}
        <div className="flex-1 min-h-0 w-full relative overflow-hidden">
          {noUrlAvailable ? (
            <div className="flex flex-col items-center justify-center h-full w-full gap-bg-v2-lg px-bg-v2-xl">
              <Icon name={IconName.WebBrowser} size="xl" variant="utility-gray" />
              <div className="flex flex-col items-center gap-bg-v2-xs">
                <Text variant="text-md" weight="semibold" color="primary" align="center">
                  No URL Available
                </Text>
              </div>
            </div>
          ) : isHttpBlocked ? (
            <div className="flex flex-col items-center justify-center h-full w-full gap-bg-v2-lg px-bg-v2-xl">
              <Icon name={IconName.Lock} size="xl" variant="utility-gray" />
              <div className="flex flex-col items-center gap-bg-v2-sm">
                <Text variant="text-md" weight="semibold" color="primary" align="center">
                  Preview not available
                </Text>
                <Text variant="text-sm" color="secondary" align="center">
                  This app is served over HTTP and cannot be embedded in a secure (HTTPS) context.
                </Text>
              </div>
              <a
                href={currentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-bg-v2-sm px-bg-v2-lg py-bg-v2-sm rounded-bg-v2-md bg-bg-secondary hover:bg-bg-tertiary border border-border-primary transition-colors text-text-primary text-sm font-medium"
              >
                <Icon name={IconName.ExternalLink} size="sm" variant="secondary" />
                Open in new tab
              </a>
            </div>
          ) : (
            <Webview
              ref={iframeRef}
              url={stableBustedUrl}
              className="w-full h-full"
              onLoad={handleWebviewLoad}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default PreviewPanel;
